import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";

import { useTranslation } from '@common/hooks/translation';

import NumericKeyboard from "../numericKeyboard";

const getValidationPattern = (isNumericString, maxDecimalCount, customFormat) => {
  if (isNumericString) return new RegExp(`^([0-9]*)$`);

  return maxDecimalCount
    ? new RegExp(`^([0-9]*)([\.]?)([0-9]{0,${maxDecimalCount}})$`)
    : new RegExp(customFormat ? "" : "^([0-9]*)([.]?)([0-9]*)$")
}

const NumericInput = ({
  value = "",
  onChange,
  defaultOpen,
  maxLength,
  customFormat,
  customButtons,
  maxDecimalCount,
  isKeyboardOpen = false,
  disableKeyboard,
  isNumericString,
  disableZero,
  placeholder,
  setFocus,
  focusInterval,
  ...rest
}) => {
  const t = useTranslation();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (setFocus) {
      if (focusInterval) {
        setTimeout(() => inputRef?.current?.focus(), focusInterval);
      } else {
        inputRef?.current?.focus();
      }
    }
  }, [defaultOpen, setFocus]);

  useEffect(() => setFocus && setKeyboardOpen(true), [setFocus]);

  const validationPattern = getValidationPattern(isNumericString, maxDecimalCount, customFormat);

  const onRemove = () => {
    if (!value) return;

    const formattedValue = value.toString();
    onChange(formattedValue.substring(0, formattedValue.length - 1));
  };

  const onValueChange = newValue => {
    if (disableZero && newValue.startsWith('0')) return;
    if (maxLength && newValue.length > maxLength) return;
    if (validationPattern.test(newValue)) onChange(newValue);
  };

  const uniqueClassName = uniqueId("numeric-message");

  return (
    <>
      <input
        {...rest}
        value={value}
        placeholder={t(placeholder)}
        className={`${rest.className} ${uniqueClassName}`}
        pattern="((.*?)\s*(\d+(?:[/-]\d+)?)?$)"
        onClick={() => !disableKeyboard && setKeyboardOpen(!keyboardOpen)}
        onChange={e => onValueChange(e.currentTarget.value)}
        ref={inputRef}
      />

      {keyboardOpen && (
        <NumericKeyboard
          customButtons={customButtons}
          onClick={sign => onValueChange(`${value}${sign}`)}
          onRemove={onRemove}
          outsideException={`.${uniqueClassName}`}
          onClose={() => setKeyboardOpen(false)}
        />
      )}
    </>
  );
};

export default NumericInput;

NumericInput.propTypes = {
  value: PropTypes.string,
  isNumericString: PropTypes.bool,
  maxDecimalCount: PropTypes.number,
  maxLength: PropTypes.number,
  customButtons: PropTypes.array,
  onChange: PropTypes.func,
  customFormat: PropTypes.bool,
  disableKeyboard: PropTypes.bool,
  disableZero: PropTypes.bool,
};
